import * as React from 'react';
import { connect } from 'react-redux';
import { assign, merge, debounce, find } from 'lodash';
import * as moment from 'moment';
import * as validator from 'validator';
import * as deepEqual from 'deep-equal';
import { push } from 'react-router-redux';
import {
  Button,
  Card,
  Icon,
  Notification,
  BookingAccessories,
  BookingDrivers,
  BookingUpdateHireForm,
  CompanyUpdateForm,
  DriverUpdateForm,
  BookingSidebar,
} from '../';
import { getBooking, updateBooking } from '../../redux/actions/bookings';
import {
  getCompany,
  searchCompanies,
  addCompany,
} from '../../redux/actions/companies';
import {
  getDrivers,
  addDriver,
  updateDriver,
} from '../../redux/actions/drivers';
import DriverSummary from '../Drivers/DriverSummary';
import ContactCollectionForm from './Collection/ContactCollectionForm';
import IContact from './Collection/IContact';
import fields from '../../data/fields';
import CompanySummary from '../Companies/CompanySummary';
import Box from '../Box/Box';

interface Props {
  section: any;
  nextSection: Function;
  previousSection: Function;
  booking: any;
  handleUpdate: Function;
  handleValidation: Function;
  updateBooking: (booking) => void;
  drivers: Array<any>;
  companyDrivers?: Array<any>;
  vehicleTypes: Array<any>;
  errors: Array<any>;
  isLoading: boolean;
  errorMessage: string | boolean;
  dispatch: Function;
  isValid: boolean;
  companies: any[];
}

interface State {
  booking: any;
  bookingSetField: Function;
  duration: number;
  error: any | boolean;
  isReady: boolean;
  hireFormError: boolean;
  driverFormError: boolean;
  pricingFormError: boolean;
  contact: IContact;
  viewSummary: boolean;
  formErrors: Array<string>;
}

// This component receives it's errors through it props when mounting
// If it has been updated, it needs to remount
// This variable is used to state if this check must be made
let checkForError: boolean = false;

class BookingCollectionValidationForm extends React.Component<Props, State> {
  state = {
    bookingSetField: () => {},
    booking: assign({}, this.props.booking, {
      drivers: [...this.props.drivers],
    }),
    duration: 0,
    error: false,
    isReady: false,
    hireFormError: false,
    driverFormError: false,
    viewSummary: true,
    pricingFormError: false,
    formErrors: [],
    contact: {
      contactType: null,
      contacts: [],
      data: {},
      contactName: this.props.booking.contact_name,
      contactEmail: this.props.booking.contact_email,
      contactPhone: this.props.booking.contact_phone_number,
    } as IContact,
  };

  private handleDurationUpdate = () => {
    const duration =
      1 +
      moment(this.state.booking.end).diff(
        moment(this.state.booking.start),
        'd',
      );
    this.setState({ duration });
  };

  /**
   * Define sections for component
   */
  async componentDidMount() {
    this.setState({ isReady: true }, () => {
      if (checkForError) {
        this.progress();
        checkForError = false;
      }
    });

    this.handleDurationUpdate();
  }

  componentWillReceiveProps() {
    this.setState({ formErrors: [] });
  }

  private handleContactSearch = debounce(
    (
      contactName: string,
      contactEmail: string = '',
      contactPhone: string = '',
    ) => {
      if (validator.isEmpty(contactName)) {
        this.setState({
          contact: merge({}, this.state.contact, {
            contacts: [],
            contactName: '',
          }),
        });
      } else {
        this.setState(
          {
            contact: merge({}, this.state.contact, {
              contactName,
              contactEmail,
              contactPhone,
              isLoadingContacts: true,
            }),
          },
          async () => {
            let response;
            switch (this.state.contact.contactType) {
              case 'driver':
                response = await this.props.dispatch(
                  getDrivers(undefined, 0, contactName),
                );
                break;
              case 'company':
                response = await this.props.dispatch(
                  searchCompanies(contactName),
                );
                break;
            }
            let contacts = [];
            if (response && !response.error) {
              contacts = response.data;
            }
            this.setState({
              contact: merge({}, this.state.contact, {
                contacts,
                isLoadingContacts: false,
              }),
            });
          },
        );
      }
    },
    200,
  );

  private changeContactType = (contactType: 'driver' | 'company') => {
    if (
      this.state.contact.contactType !== contactType &&
      !this.state.contact.assignedContact
    ) {
      const { contactName, contactEmail, contactPhone } = this.state.contact;
      this.setState({
        contact: {
          contactType,
          contactName,
          contactEmail,
          contactPhone,
          contacts: [],
          data: {},
        },
      });
    }
  };

  private setContact = (assignedContact) => {
    if (!this.state.contact.assignedContact) {
      const data = this.state.contact.data;
      switch (this.state.contact.contactType) {
        case 'company':
          data.company_id = assignedContact.id;
          break;
        case 'driver':
          data.driver_id = assignedContact.id;
          break;
      }
      this.setState({
        contact: merge({}, this.state.contact, {
          data,
          assignedContact,
        }),
      });
    }
  };

  private unsetContact = () => {
    this.setState({
      contact: merge({}, this.state.contact, {
        assignedContact: false,
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        data: undefined,
      }),
    });
  };

  /**
   * Return whether the booking has been updated
   */
  private isChanged = () => {
    return !deepEqual(this.state.booking, this.props.booking);
  };

  private handleDriverUpdate = (drivers) => {
    const { booking } = this.state;
    booking.drivers = drivers;
    this.setState({ booking });
  };

  private handleAccessoriesUpdate = (accessories, cost) => {
    const { booking } = this.state;
    booking.accessories = accessories;
    booking.accessory_cost = cost;
    this.setState({ booking });
  };

  /**
   * Go to the next section if there are no errors
   */
  private progress = () => {
    const currentSection = this.props.section;
    const errors = this.getErrorsForSection(currentSection);
    window.scrollTo(0, 0);
    if (!errors.errors.length) {
      this.props.updateBooking(this.state.booking);
      this.props.nextSection();
      this.setState({ viewSummary: true });
    }
  };

  /**
   * Progress if the form has been updated, otherwise delay progression
   */
  private delayedProgression = (update: boolean) => {
    if (!update) {
      this.progress();
    } else {
      checkForError = true;
    }
  };

  // Handle updating a booking and performing a revalidation check
  private handleBookingSubmit = async (bookingData = this.props.booking) => {
    if (this.state.isReady && !this.state.error) {
      this.setState({ isReady: false, error: false });
      if (!validator.isNumeric(String(bookingData.company_id))) {
        bookingData.company_id = '';
      }
      // Drivers updated are attached to the state
      const booking = merge({}, bookingData, {
        updateHireCost: true,
        drivers: JSON.stringify(
          this.state.booking.drivers.map((driver) => driver.id),
        ),
        accessories: JSON.stringify(
          bookingData.accessories.map((accessory) => ({
            id: accessory.id,
            quantity: accessory.quantity,
            total: accessory.total,
          })),
        ),
      });
      const response = await this.props.dispatch(
        updateBooking(this.props.booking.id, booking),
      );
      if (response.error) {
        this.setState({ error: response.error, isReady: true });
        return;
      }
      await this.props.handleValidation(response.data.id);
      await this.props.handleUpdate(response);
      this.setState({ isReady: true });
    }
  };

  /**
   * Update all linked resources after the company provided has been updated
   * @param {Number} oldId
   * @param {Company} company
   */
  private handlePostCompanyUpdate = async (oldId: number, company: any) => {
    let { booking } = this.props;
    const drivers = booking.drivers.filter(
      (driver) => driver.company_id === oldId,
    );

    // Update the drivers connected to company to point at new version
    // Reputation defaults to null if not provided in update request
    await Promise.all(
      drivers.map(async (driver) =>
        this.props.dispatch(
          updateDriver(driver.id, {
            company_id: company.id,
            reputation: driver.reputation,
          }),
        ),
      ),
    );
    // Update booking to point to new driver versions
    booking = await this.updateBookingDrivers(
      drivers.map((driver) => driver.id),
    );

    // If the company has a newest field update the company_id
    if (booking.company_id === oldId) {
      booking.company_id = company.id;
    }

    // Update the company
    this.setState({ booking });
    this.handleBookingSubmit(booking);
  };

  private getErrorsForSection = (section) => {
    // If the model is not found return an empty array of errors
    return (
      find(this.props.errors, {
        id: section.id as any,
        model: section.model as any,
      }) || {
        errors: [],
      }
    );
  };

  /**
   * Update the specified drivers for the booking to the newest versions
   * @param {Array<number>} ids The ids of the drivers to update
   */
  private updateBookingDrivers = async (ids: Array<number>) => {
    const response = await this.props.dispatch(
      getBooking(this.props.booking.id),
    );
    if (response.error) {
      this.setState({ error: response.error, isReady: true });
      return null;
    }
    return response.data;
  };

  private renderProblemsSection = () => {
    const currentSection = this.props.section;
    if (currentSection === undefined) {
      return null;
    }

    if (currentSection.model === 'type') {
      if (!this.state.error) {
        return null;
      }
      return <Notification type="danger">{this.state.error}</Notification>;
    }

    const errors = this.state.formErrors.length
      ? { errors: this.state.formErrors.map((error) => ({ error })) }
      : this.getErrorsForSection(currentSection);

    return (
      <Box>
        <h4 className="heading">Issues</h4>
        {errors &&
          errors.errors.map((error, index) => (
            <div className="level" key={index}>
              <div className="level-left">
                <div className="level-item">
                  <Icon icon="times has-text-danger" size="large" />
                </div>
                <div className="level-item">
                  <strong>{error.error}</strong>
                </div>
              </div>
            </div>
          ))}
        {!errors.errors.length && (
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <Icon icon="check has-text-success" size="large" />
              </div>
              <div className="level-item has-text-grey">
                No issues found on this page
              </div>
            </div>
          </div>
        )}
      </Box>
    );
  };

  private renderContactSection = () => (
    <ContactCollectionForm
      booking={this.state.booking}
      contact={this.state.contact}
      setContact={this.setContact}
      unsetContact={this.unsetContact}
      isLoading={this.props.isLoading || !this.state.isReady}
      handleContactSearch={this.handleContactSearch}
      changeContactType={this.changeContactType}
      addCompany={async (company) => this.props.dispatch(addCompany(company))}
      addDriver={async (driver) => this.props.dispatch(addDriver(driver))}
      handleError={(error) => {
        this.setState({ error });
      }}
      handleClick={async () => {
        this.delayedProgression(this.isChanged());
        if (this.isChanged()) {
          await this.handleBookingSubmit(this.state.booking);
        }
      }}
    />
  );

  /**
   * Render a collection validation screen for a driver
   */
  private renderDriverSection = () => {
    const section = this.props.section;

    const driver = find(this.props.drivers, { id: section.id as any });
    if (driver === undefined) {
      return null;
    }

    const company = driver.company_id
      ? find(this.props.companies, { id: driver.company_id })
      : {};

    if (this.state.viewSummary) {
      return (
        <>
          <Notification type="warning">
            Please check with the customer that these driver details are up to
            date
          </Notification>
          <Box>
            <DriverSummary company={company} driver={driver} />
          </Box>
          <div className="buttons is-right">
            <button
              className="button is-default"
              onClick={() => this.setState({ viewSummary: false })}
            >
              <Icon icon="pencil" />
              <span>Edit</span>
            </button>
            {!this.props.booking.company_id && (
              <button
                className="button is-default"
                onClick={() => {
                  if (
                    !window.confirm(
                      'Are you sure you want to change the ' +
                        'lead driver? You will exit the collection flow',
                    )
                  ) {
                    return;
                  }
                  this.props.dispatch(
                    push(`/bookings/${this.props.booking.id}?section=1`),
                  );
                }}
              >
                <Icon icon="undo" />
                <span>Change Lead Driver</span>
              </button>
            )}
            <button className="button is-outlined" onClick={this.progress}>
              <span>Continue</span>
              <Icon icon="arrow-right" />
            </button>
          </div>
        </>
      );
    }

    return (
      <DriverUpdateForm
        key={section.id}
        driver={driver}
        updateButtonText="Save and Continue"
        backButtonText="Back"
        isSectioned
        onErrors={(formErrors) => this.setState({ formErrors })}
        handleUpdate={async (driver, isChanged) => {
          this.delayedProgression(isChanged);
          if (isChanged) {
            if (driver === undefined) {
              this.setState({
                error: 'There was an error with the drivers fields',
                isReady: true,
              });
              return;
            }
            const booking = await this.updateBookingDrivers([driver.id]);
            if (booking !== null) {
              this.setState({ booking });
              this.handleBookingSubmit();
            }
          }
        }}
      />
    );
  };

  /**
   * Render a collection validation screen for a company
   */
  private renderCompanySection = () => {
    const section = this.props.section;

    const company = find(this.props.companies, { id: section.id as any });
    if (company === undefined) {
      return null;
    }

    if (this.state.viewSummary) {
      return (
        <>
          <Notification type="warning">
            Please check with the customer that these company details are up to
            date
          </Notification>
          <Box>
            <CompanySummary company={company} />
          </Box>
          <div className="buttons is-right">
            <button
              className="button is-default"
              onClick={() => this.setState({ viewSummary: false })}
            >
              <Icon icon="pencil" />
              <span>Edit</span>
            </button>
            <button className="button is-outlined" onClick={this.progress}>
              <span>Continue</span>
              <Icon icon="arrow-right" />
            </button>
          </div>
        </>
      );
    }

    return (
      <CompanyUpdateForm
        key={section.id}
        company={company}
        backButtonText="Back"
        updateButtonText="Save and Continue"
        onErrors={(formErrors) => this.setState({ formErrors })}
        isSectioned
        handleUpdate={async (updatedCompany, isChanged) => {
          this.delayedProgression(isChanged);
          if (isChanged) {
            const response = await this.props.dispatch(
              getCompany(updatedCompany.id),
            );
            if (response.error) {
              this.setState({ error: response.error, isReady: true });
              return;
            }
            await this.handlePostCompanyUpdate(
              updatedCompany.id,
              response.data,
            );
          }
        }}
      />
    );
  };

  /**
   * Render a collection validation screen for adding more drivers
   */
  private renderDriverSelectionSection = () => (
    <>
      <Notification type="warning">
        Do you need to add or remove any drivers?
      </Notification>
      <BookingDrivers
        list
        disableFirstDriverRemove={!this.props.booking.company_id}
        handleUpdate={this.handleDriverUpdate}
        booking={this.state.booking}
        isDisabled={!this.state.isReady}
        companyDrivers={this.props.companyDrivers}
      />
      <div className="buttons is-right">
        <Button
          className={`is-primary is-outlined\
          ${this.props.isLoading || !this.state.isReady ? ' is-loading' : ''}`}
          handleClick={async () => {
            const badDrivers = this.state.booking.drivers.filter((driver) => {
              return driver.reputation === 2;
            });
            const badDriversErrorMessage =
              'Drivers with bad reputation cannot collect bookings';
            if (
              badDrivers.length > 0 &&
              !this.state.formErrors.includes(badDriversErrorMessage)
            ) {
              this.setState({
                formErrors: [...this.state.formErrors, badDriversErrorMessage],
              });
              this.renderProblemsSection();
            }

            if (badDrivers.length <= 0) {
              this.delayedProgression(this.isChanged());
            }

            if (this.isChanged() && badDrivers.length <= 0) {
              await this.handleBookingSubmit(this.state.booking);
            }
          }}
        >
          <span>Continue</span>
          <Icon icon="arrow-right" />
        </Button>
      </div>
    </>
  );

  private renderAccessoriesSection = () => (
    <>
      <Notification type="warning">Any accessories?</Notification>
      <BookingAccessories
        list
        handleUpdate={this.handleAccessoriesUpdate}
        booking={this.state.booking}
        duration={this.state.duration}
        isDisabled={!this.state.isReady}
      />
      <div className="buttons is-right">
        <Button
          className={`is-primary is-outlined\
                ${
                  this.props.isLoading || !this.state.isReady
                    ? ' is-loading'
                    : ''
                }`}
          handleClick={async () => {
            this.delayedProgression(this.isChanged());
            if (this.isChanged()) {
              await this.handleBookingSubmit(this.state.booking);
            }
          }}
        >
          <span>Continue</span>
          <Icon icon="arrow-right" />
        </Button>
      </div>
    </>
  );

  private renderBookingSection = () => {
    const handleUpdate = (data) => {
      this.setState(
        { booking: merge({}, this.state.booking, data) },
        this.handleDurationUpdate,
      );
    };
    const validateForms = () => {
      const { hireFormError, driverFormError, pricingFormError } = this.state;
      if (hireFormError || driverFormError || pricingFormError) {
        this.setState({
          error: 'Some of the fields for this booking are invalid',
        });
      } else {
        this.setState({ error: false });
      }
    };
    // Duration set as BookingAccessories key to force it to re-render and recalculate totals
    return (
      <>
        <Notification type="warning">
          Is the hire information correct?
        </Notification>
        <Card>
          <BookingUpdateHireForm
            handleUpdate={handleUpdate}
            handleError={(hireFormError) => {
              this.setState({ hireFormError }, validateForms);
            }}
            updateOnChange
            booking={this.state.booking}
            isDisabled={!this.state.isReady}
            fields={fields.bookings.updateCollection}
          />
        </Card>
        <div className="buttons is-right">
          <Button
            className={`is-primary is-outlined\
                ${
                  this.props.isLoading || !this.state.isReady
                    ? ' is-loading'
                    : ''
                }`}
            handleClick={async () => {
              this.delayedProgression(this.isChanged());
              if (this.isChanged()) {
                await this.handleBookingSubmit(this.state.booking);
              }
            }}
          >
            <span>Continue</span>
            <Icon icon="arrow-right" />
          </Button>
        </div>
      </>
    );
  };

  /**
   * Render the current section selected
   */
  private renderSection = () => {
    switch (this.props.section.model) {
      case 'driver':
        return this.renderDriverSection();
      case 'company':
        return this.renderCompanySection();
      case 'type':
        return this.renderContactSection();
      case 'drivers':
        return this.renderDriverSelectionSection();
      case 'accessories':
        return this.renderAccessoriesSection();
      default:
        return this.renderBookingSection();
    }
  };

  render() {
    return (
      <div className="column">
        <div className="columns">
          <div className="column is-8-desktop is-7-widescreen">
            {this.renderSection()}
          </div>
          <div className="column is-4-desktop is-5-widescreen">
            {this.renderProblemsSection()}
            <BookingSidebar
              booking={this.props.booking}
              duration={this.state.duration}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default connect()(BookingCollectionValidationForm);
